import { Component, ElementRef, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output, signal, ViewChild } from '@angular/core';
import { UserActivityType } from '@app/model/enums/user-activity-type.enum';
import { SlideDTO } from '@app/model/slideDTO.model';
import { SettingsService } from '@app/services/settings.service';
import { UserActivityService } from '@app/services/user-activity.service';
import { EntityTypeId, LearningPathCardComponent, NodeModel, SettingsColors, SlideType, AnimationOption, SlideHeightControlMode } from 'library-explorer';

import { Router } from '@angular/router';
import { LearningPathModel } from '@app/model/learning-path.model';
import { LearningPathService } from '@app/services/api/learning-path.service';
import { LanguageService } from '@app/services/language.service';
import { PayableNodeNavigationService } from '@app/services/payable-node-navigation.service';
import { BaseSlideWidgetComponent } from '../base-slide-widget/base-slide-widget';
import { SlideBuilderComponent } from '../slide-builder/slide-builder.component';

@Component({
    selector: 'app-general-widget',
    templateUrl: './general-widget.component.html',
    styleUrls: ['./general-widget.component.scss'],
    standalone: false,
    host: {
      'class': 'widget-container',
      '[class.has-animation]': 'data.animation',
    }
})
export class GeneralWidgetComponent extends BaseSlideWidgetComponent implements OnInit, OnDestroy {
  @ViewChild(SlideBuilderComponent) slideBuilderComponent: SlideBuilderComponent;
  @ViewChild('container', { static: false }) set container(value: ElementRef<HTMLElement>) {
    if (this.containerHeight) {
      return;
    }

    this.containerHeight = this.parentContainerHeight || value.nativeElement.offsetHeight;
  }

  @Input() public isQuiz = false;
  @Input() public data: SlideDTO;
  @Input() public parentContainerHeight: number;
  @Input() public isSlideBuilder = false;
  @Input() public requiredToWatchVideo: boolean;
  @Input() public requiredToCompleteInteraction: boolean;
  @Input() public brandColors: SettingsColors;

  @Input() public chapterContainerHeight: number;

  public get adjustedChapterContainerHeight(): number {
    if (!this.chapterContainerHeight) {
      return this.chapterContainerHeightLimit;
    }
  
    const adjustedHeight = this.chapterContainerHeight - 60;
    return adjustedHeight > this.chapterContainerHeightLimit ? adjustedHeight : this.chapterContainerHeightLimit;
  }

  @Output() public videoWatched: EventEmitter<SlideDTO> = new EventEmitter();
  @Output() public interactionCompleted: EventEmitter<{ slide: SlideDTO, completionRatio: number }> = new EventEmitter();

  public animation: AnimationOption;
  public animationDelay: number;
  public slideHidden = signal(false);
  public slideTypes: typeof SlideType = SlideType;
  public skipAnimationForHost = false;
  public containerHeight = 0;
  public forceFullScreen = false;
  public preventImageDownload = false;
  public imageWatermarkEnabled: boolean;
  public libraryDocumentDownload = false;
  public isRtl = false;
  public readonly heightModes: typeof SlideHeightControlMode = SlideHeightControlMode;

  private readonly chapterContainerHeightLimit = 400;

  constructor(
    private readonly payableNodeNavigationService: PayableNodeNavigationService,
    private readonly learningPathService: LearningPathService,
    private readonly router: Router,
    private readonly userActivityService: UserActivityService,
    private readonly settingsService: SettingsService,
    private readonly languageService: LanguageService,
    protected readonly element: ElementRef) {
    super(element);
  }

  ngOnInit() {
    const slides = [
      SlideType.SlideAccordion, 
      SlideType.SlideTextFlipcards, 
      SlideType.SlideBuckets,
      SlideType.SlideDocuments,
      SlideType.SlideInstaLearnList,
      SlideType.SlideText
    ];
  
    this.skipAnimationForHost = slides.indexOf(this.data.type as SlideType) !== -1;
    this.isRtl = this.languageService.isRtl();

    if (this.isSlideBuilder && this.data.animation) {
      this.animation = this.data.animation;
      this.animationDelay = this.data.animationDelay || 0;

      if (this.animation) {
        this.slideHidden.set(true);
        this.addIntersectionObserver();
      }
    }

    this.settingsService.getSettings().subscribe(data => {
      this.forceFullScreen = data.lesson.forceFullscreen;
      this.preventImageDownload = !!data.security.preventImageDownload;
      this.imageWatermarkEnabled = data.security.watermark?.enableWatermarkForImages;
      this.libraryDocumentDownload = data.lesson.libraryDocumentDownload;
      this.brandColors = data.colors;
    });

    this.element.nativeElement.style.setProperty('--lms-widget-max-width', this.data.maxWidth);
  }

  ngOnDestroy() {
    this.removeIntersectionObserver();
  }

  public emitVideoWatched(slide = this.data, logActivity = true): void {
    if (logActivity) {
      this.logUserActivity(UserActivityType.USER_WATCHED_VIDEO);
    }

    this.videoWatched.emit(slide);
  }

  public videoStarted(): void {
    this.logUserActivity(UserActivityType.USER_START_WATCHING_VIDEO);
  }

  public slideInteractionCompleted(data: { slide: SlideDTO, completionRatio: number }): void {
    const { slide, completionRatio } = data;
    this.interactionCompleted.emit({ slide, completionRatio: completionRatio || 1 });
  }

  public navigateToPath(path: LearningPathModel): void {
    this.router.navigate(['/learning-path', path.id]);
  }

  public startPath({ item, component }: { item: LearningPathModel, component: LearningPathCardComponent }): void {
    component.startButtonLoading.next(true);

    this.learningPathService.getLearningPath(item.id).subscribe(learningPath => {
      this.learningPathService.startLearningPath(learningPath, component.startButtonLoading);
    })
  }

  public openDetails(item: any): void {
    this.payableNodeNavigationService.openCourseDetails(item, true).subscribe();
  }

  public navigate({ event, item }: { event: MouseEvent, item: NodeModel }): void {
    this.payableNodeNavigationService.navigate(item, event);
  }

  private logUserActivity(type: UserActivityType): void {
    this.userActivityService.logUserVideoActivity(type, this.data.id, EntityTypeId.PARAGRAPH, this.data.mediaVideo);
  }
}
